import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import PageHeader from '@/components/feature/PageHeader';
import SectionHeading from '@/components/base/SectionHeading';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useHashScroll } from '@/hooks/useHashScroll';
import { siteStats } from '@/mocks/home';

const focuses = [
  {
    icon: 'ri-newspaper-line',
    title: '资讯聚合',
    desc: '新番定档、制作动态、声优活动与展会信息，用编辑判断代替信息堆叠，只留下值得知道的那部分。',
  },
  {
    icon: 'ri-book-open-line',
    title: '作品档案',
    desc: '为每部作品建立可检索的档案：类型、地区、年份、主创与剧情简述，方便你判断它是否适合自己。',
  },
  {
    icon: 'ri-quill-pen-line',
    title: '文化解读',
    desc: '把作品放回它的文化语境，讨论美学、分镜、产业与社群，让喜欢动画这件事有更多层次。',
  },
];

const values = [
  {
    index: '01',
    title: '慢一点，但准确',
    desc: '宁可晚半天发布，也要把制作公司、播出时间与主创信息核对清楚。资讯的价值在于可信。',
  },
  {
    index: '02',
    title: '把读者当同好',
    desc: '不居高临下地科普，也不迎合情绪。用平等的语气分享我们真正感兴趣的东西。',
  },
  {
    index: '03',
    title: '尊重创作与版权',
    desc: '所有配图均为演示素材，作品名称与商标归各自权利人所有。我们支持正版观看与购买。',
  },
  {
    index: '04',
    title: '保留讨论空间',
    desc: '文化内容没有唯一答案。我们提供视角，而不是结论，欢迎不同意见在评论区与来信中发生。',
  },
];

const workflow = [
  { step: '选题', desc: '每周编辑会，从读者来信与业界动态中筛选议题。' },
  { step: '资料', desc: '核对官方公告、访谈与公开数据，建立事实清单。' },
  { step: '写作', desc: '由熟悉该领域的作者执笔，保留个人判断与语气。' },
  { step: '校对', desc: '事实复核与文字打磨，配图统一风格后发布。' },
];

export default function AboutPage() {
  usePageMeta({
    title: '关于我们 - 琉璃神社',
    description:
      '了解琉璃神社的定位、团队理念与内容原则，以及内容投稿、版权合作与联系方式。琉璃神社专注动漫资讯与文化分享。',
    keywords: '关于琉璃神社,动漫文化平台,内容团队,投稿合作,版权声明',
  });
  useHashScroll();

  return (
    <SiteLayout>
      <PageHeader
        eyebrow="About"
        title="关于琉璃神社"
        description="一个由爱好者组成的内容编辑团队，专注动漫资讯与文化分享。我们相信，热爱值得被认真记录。"
        breadcrumb={[{ label: '关于我们' }]}
      />

      <div className="shell py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-px bg-accent-500"></span>
              <span className="text-[11px] tracking-[0.24em] uppercase text-accent-600 font-label">
                Our Position
              </span>
            </div>
            <h2 className="text-xl md:text-3xl font-semibold leading-relaxed">
              专业动漫文化资讯平台
            </h2>
            <p className="mt-5 text-sm md:text-[15px] text-foreground-700 leading-7 md:leading-8">
              琉璃神社聚合动画、漫画及二次元周边内容，服务热爱动漫文化的年轻群体、动漫文化研究者与二次元玩家。我们希望解决一个具体的问题：在信息过载的环境里，如何更快地找到可信的动态，又如何更深地理解一部作品。
            </p>
            <p className="mt-3 text-sm md:text-[15px] text-foreground-700 leading-7 md:leading-8">
              因此我们把内容分成三条线：资讯负责“发生了什么”，作品档案负责“这是什么”，文化专题负责“为什么值得被记住”。三者互相链接，构成一个可以持续浏览的内容网络。
            </p>
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {siteStats.map((stat) => (
                <div key={stat.label} className="rounded-lg border border-background-200/80 bg-background-100/60 px-3 py-3">
                  <p className="text-lg md:text-xl font-label text-primary-700">{stat.value}</p>
                  <p className="mt-1 text-[11px] text-secondary-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="w-full aspect-[4/3] rounded-lg overflow-hidden border border-background-200/80 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Anime%20style%20illustration%20of%20a%20cozy%20editorial%20studio%20with%20wooden%20desks%2C%20stacked%20books%2C%20anime%20posters%20and%20warm%20lamps%2C%20large%20window%20with%20teal%20evening%20light%2C%20two%20people%20discussing%20storyboards%2C%20painterly%20digital%20art%2C%20detailed%20warm%20interior%20composition&width=1200&height=900&seq=about-editorial-studio&orientation=landscape"
                alt="琉璃神社编辑工作室插画"
                title="琉璃神社 - 编辑工作室"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <p className="mt-2.5 text-[11px] text-secondary-400 text-center">编辑工作室 · 演示插图</p>
          </div>
        </div>
      </div>

      <div className="w-full bg-background-100/70 border-y border-background-200/70 py-10 md:py-14">
        <div className="shell">
          <SectionHeading
            eyebrow="What We Do"
            title="我们关注的三件事"
            description="从即时动态到长期阅读，三条内容线各自独立，也彼此连接。"
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {focuses.map((focus) => (
              <div
                key={focus.title}
                className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6"
              >
                <span className="w-11 h-11 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center">
                  <i className={`${focus.icon} text-lg`}></i>
                </span>
                <h3 className="mt-4 text-base font-semibold">{focus.title}</h3>
                <p className="mt-2 text-sm text-secondary-600 leading-7">{focus.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div id="team" className="shell py-10 md:py-16 scroll-mt-24">
        <SectionHeading
          eyebrow="Our Values"
          title="团队理念"
          description="我们没有宏大的口号，只有几条愿意长期遵守的规则。"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {values.map((value) => (
            <div
              key={value.index}
              className="flex gap-4 rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6"
            >
              <span className="text-2xl font-label text-accent-500 leading-none">{value.index}</span>
              <div>
                <h3 className="text-sm md:text-base font-semibold">{value.title}</h3>
                <p className="mt-2 text-sm text-secondary-600 leading-7">{value.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10">
          <h2 className="text-base md:text-lg font-semibold mb-4">一条内容是怎么做出来的</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {workflow.map((flow, index) => (
              <div key={flow.step} className="rounded-lg bg-background-100 border border-background-200/70 p-4">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary-500 text-background-50 text-[11px] flex items-center justify-center font-label">
                    {index + 1}
                  </span>
                  <p className="text-sm font-medium">{flow.step}</p>
                </div>
                <p className="mt-2 text-xs text-secondary-600 leading-6">{flow.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div id="contact" className="w-full bg-primary-950 py-10 md:py-16 scroll-mt-24">
        <div className="shell">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-6 h-px bg-accent-500"></span>
            <span className="text-[11px] tracking-[0.24em] uppercase text-accent-300 font-label">
              Contact & Cooperation
            </span>
          </div>
          <h2 className="text-xl md:text-3xl font-semibold text-background-50">联系与合作</h2>
          <p className="mt-3 text-sm text-background-100/70 leading-7 max-w-2xl">
            欢迎内容投稿、选题建议、版权合作与媒体联络。我们通常会在三个工作日内回复。
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-background-50/15 bg-background-50/5 p-5 md:p-6">
              <h3 className="text-sm font-semibold text-background-50">内容投稿</h3>
              <p className="mt-2 text-xs text-background-100/65 leading-6">
                动漫评论、作品解析、文化观察均可。请附上作者简介与过往作品链接，字数建议 2,000 字以上。
              </p>
              <a
                href="mailto:hello@rurishrine.example.com"
                className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-md bg-background-50 text-primary-900 text-xs hover:bg-accent-500 hover:text-primary-950 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-send-line w-4 h-4 flex items-center justify-center"></i>
                投稿邮箱
              </a>
            </div>
            <div className="rounded-lg border border-background-50/15 bg-background-50/5 p-5 md:p-6">
              <h3 className="text-sm font-semibold text-background-50">商务与版权</h3>
              <p className="mt-2 text-xs text-background-100/65 leading-6">
                作品宣传、活动报道、内容授权与联合企划。请说明合作背景、时间节点与预期形式。
              </p>
              <a
                href="mailto:hello@rurishrine.example.com"
                className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-md border border-background-50/30 text-background-50 text-xs hover:border-background-50/70 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-team-line w-4 h-4 flex items-center justify-center"></i>
                合作联络
              </a>
            </div>
          </div>
        </div>
      </div>

      <div id="legal" className="shell py-10 md:py-16 scroll-mt-24">
        <SectionHeading eyebrow="Legal" title="版权声明与免责声明" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
          <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
            <h3 className="text-sm font-semibold">版权声明</h3>
            <p className="mt-2.5 text-sm text-secondary-600 leading-7">
              本站原创文字内容版权归琉璃神社及署名作者所有，转载请注明来源并保留原文链接。站内提及的作品名称、角色名称、商标及标识归各自权利人所有，仅用于介绍、评论与文化讨论。
            </p>
            <p className="mt-2.5 text-sm text-secondary-600 leading-7">
              本站页面所使用的配图均为演示用生成素材，不代表任何真实作品的官方视觉，亦不用于商业用途。
            </p>
          </div>
          <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-6">
            <h3 className="text-sm font-semibold">免责声明</h3>
            <p className="mt-2.5 text-sm text-secondary-600 leading-7">
              本站为演示站点，所有资讯、作品资料、评分与统计数据均为示例内容，不构成任何事实陈述或购买建议。播出时间、制作阵容等信息请以官方公告为准。
            </p>
            <p className="mt-2.5 text-sm text-secondary-600 leading-7">
              站内搜索、筛选与分享功能均在前端完成，不收集、不存储用户个人信息，也不提供账号、支付、收藏或下载服务。
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 rounded-lg bg-background-100 border border-background-200/70 px-5 py-4">
          <p className="text-xs text-secondary-600">想看看我们最近在写什么？</p>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/category/culture"
              className="inline-flex items-center justify-center h-9 px-4 rounded-md bg-primary-500 text-background-50 text-xs hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              浏览文化专题
            </Link>
            <Link
              to="/category/news"
              className="inline-flex items-center justify-center h-9 px-4 rounded-md border border-background-200 bg-background-50 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              查看最新资讯
            </Link>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}